import React, { FC, useContext, useEffect } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import Routes from './routes/Routes';
import { ThemeContext } from './contexts/ThemeContext';
import { useCurrentUser, UserProvider } from './contexts/UserContext';
import { ThemeColorContext } from './contexts/ThemeColorContext';
import './App.css';
import { useViewerStore } from './store/store';
import { installAiResultCacheTabLifecycle } from './services/geminiService';
import { UnitContext } from './contexts/UnitContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomNavigationClient } from './utils/NavigationClient';
import { MsalProvider } from '@azure/msal-react';
import type { PublicClientApplication } from '@azure/msal-browser';


interface AppProps {
    pca: PublicClientApplication;
}

const App: FC<AppProps> = ({ pca }) => {
    const navigate = useNavigate();

    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    return (
        <MsalProvider instance={pca}>
            <UserProvider>
                <Pages />
            </UserProvider>
        </MsalProvider>
    );
};



const Pages: FC = () => {
    const { theme } = useContext(ThemeContext);
    const { user } = useCurrentUser();
    const { primary } = useContext(ThemeColorContext);
    const unitContext = useContext(UnitContext);

    const isIframeEmbeddedInOneButton = useViewerStore(
        (s) => s.isIframeEmbeddedInOneButton,
    );
    const isDashboardOpen = useViewerStore((s) => s.isDashboardOpen);
    const initialDashboardReady = useViewerStore((s: any) => s.initialDashboardReady);

    useEffect(() => {
        useViewerStore.setState({unitContext : unitContext})
    }, [unitContext]);

    useEffect(() => {
        installAiResultCacheTabLifecycle();
    }, []);

    const location = useLocation();
    return (
        <>
            <div
                className='app'
                data-route={location.pathname}
                data-isiframeembeddedinonebutton={isIframeEmbeddedInOneButton}
                data-theme={theme}
                data-user={JSON.stringify(user)}
                data-dashboard-open={isDashboardOpen}
                data-dashboard-initialready={initialDashboardReady}
            >
                <ConfigProvider
                    theme={{
                        algorithm:
                            theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                        token: {
                            colorPrimary: primary,
                            colorBgBase: theme === 'dark' ? '#141414' : '#fafbfc',
                            colorBgContainer: theme === 'dark' ? '#262626' : '#ffffff',
                            colorBgElevated: theme === 'dark' ? '#303030' : '#ffffff',
                            colorText: theme === 'dark' ? 'rgba(255,255,255,0.9)' : '#171a1f',
                            colorTextSecondary: theme === 'dark' ? 'rgba(255,255,255,0.55)' : '#565d6d',
                            colorTextTertiary: theme === 'dark' ? 'rgba(255,255,255,0.45)' : '#9095a1',
                            colorBorder: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb',
                            colorBorderSecondary: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#eef0f3',
                            borderRadius: 8,
                            wireframe: false,
                            fontFamily: "'Inter', sans-serif",
                            fontSize: 14,
                            fontSizeLG: 16,
                            fontSizeSM: 12,
                            lineHeight: 1.5715,
                            controlHeight: 40,
                            controlHeightLG: 48,
                            controlHeightSM: 32,
                            paddingContentHorizontal: 16,
                            paddingContentVertical: 12,
                            padding: 16,
                            paddingSM: 12,
                            paddingXS: 8,
                            paddingLG: 24,
                            marginXS: 8,
                            marginSM: 12,
                            margin: 16,
                            marginMD: 20,
                            marginLG: 24,
                            marginXL: 32,
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                            boxShadowSecondary: '0 4px 16px rgba(15, 23, 42, 0.08)',
                        },
                        components: {
                            Button: {
                                paddingInline: 16,
                                paddingBlock: 8,
                                contentFontSize: 14,
                                primaryShadow: 'none',
                                defaultShadow: 'none',
                                ...(theme === 'dark'
                                    ? {
                                          defaultBg: '#262626',
                                          defaultBorderColor: 'rgba(255, 255, 255, 0.1)',
                                          defaultColor: 'rgba(255, 255, 255, 0.88)',
                                          defaultHoverBg: '#303030',
                                          defaultHoverBorderColor: 'rgba(255, 255, 255, 0.14)',
                                          defaultHoverColor: '#ffffff',
                                          defaultActiveBg: '#303030',
                                          defaultActiveBorderColor: 'rgba(255, 255, 255, 0.14)',
                                      }
                                    : {}),
                            },
                            Select: {
                                ...(theme === 'dark'
                                    ? {
                                          selectorBg: '#262626',
                                          optionSelectedBg: `color-mix(in srgb, ${primary} 22%, #262626)`,
                                          optionActiveBg: `color-mix(in srgb, ${primary} 16%, #303030)`,
                                      }
                                    : {}),
                            },
                            Input: {
                                paddingBlock: 10,
                                paddingInline: 14,
                            },
                            Table: {
                                headerBg: theme === 'dark' ? '#262626' : '#f4f6f8',
                                headerColor: theme === 'dark' ? 'rgba(255,255,255,0.9)' : '#171a1f',
                                headerSplitColor: 'transparent',
                                borderColor: theme === 'dark' ? '#334155' : '#e8eaef',
                                cellPaddingBlock: 12,
                                cellPaddingInline: 16,
                            },
                            Modal: {
                                paddingMD: 24,
                                paddingContentHorizontal: 24,
                            },
                            /* Sit above pricing/AHJ modals (9600–9610), drawers (~9250), editor modals (~10020), fixed header (10050) */
                            Notification: {
                                zIndexPopup: 20000,
                            },
                            Message: {
                                zIndexPopup: 11000,
                            },
                            Card: {
                                paddingLG: 24,
                            },
                            Tabs: {
                                horizontalMargin: '0 0 12px 0',
                            },
                            Form: {
                                itemMarginBottom: 16,
                                labelFontSize: 13,
                            },
                        },
                    }}
                >
                    <Routes />
                </ConfigProvider>
            </div>
        </>
    );
};

export default App;
